import { atom } from 'jotai';

import { inventoryItemsQueryAtom } from '../_graphql/queryInventoryItems';
import { inventoryTagsQueryAtom } from '../_graphql/queryInventoryTags';

import {
  $effectiveLineItems,
  $remoteSalesOrderDetail,
} from './salesOrderDetail.state';

const INVENTORY_ITEMS_QUERY_LIMIT = 200;

export type ItemAvailability = {
  itemId: string;
  itemName: string;
  looseWeight: number;
  taggedWeight: number;
};

// 🔘🔘🔘 PLAIN ATOMS - Input state
export const $inventoryAvailabilityVisible = atom<boolean>(false);

// 🟢🟢🟢 DERIVED DATA - Item IDs from effective line items
export const $inventoryAvailabilityItemIds = atom((get) => {
  const lineItems = get($effectiveLineItems);
  const itemIds = lineItems
    .map((item) => item.itemId)
    .filter((id): id is string => id !== null && id !== undefined && id !== '');
  return [...new Set(itemIds)];
});

// 🟢🟢🟢 DERIVED DATA - Facility ID from sales order detail
export const $inventoryAvailabilityFacilityData = atom((get) => {
  const salesOrderData = get($remoteSalesOrderDetail);
  return salesOrderData?.data?.facility ?? null;
});

// 🟢🟢🟢 REMOTE DATA - Inventory items
export const $inventoryItems = inventoryItemsQueryAtom(() => ({
  variables: {
    limit: INVENTORY_ITEMS_QUERY_LIMIT,
  },
}));

// 🟢🟢🟢 REMOTE DATA - Inventory tags filtered by itemIds and facilityId
export const $inventoryTagsByItemIds = inventoryTagsQueryAtom((get) => {
  const itemIds = get($inventoryAvailabilityItemIds);
  const facility = get($inventoryAvailabilityFacilityData);

  return {
    variables: {
      itemIds: itemIds,
      facilityId: facility?.id ?? undefined,
    },
    skip: itemIds.length === 0 || !facility,
  };
});

// 🟢🟢🟢 DERIVED DATA - Items by ID lookup
export const $itemsById = atom((get) => {
  const { data: itemsData } = get($inventoryItems);
  return itemsData?.itemsById ?? {};
});

// 🟢🟢🟢 DERIVED DATA - Inventory availability loading state
export const $isInventoryAvailabilityLoading = atom((get) => {
  const itemIds = get($inventoryAvailabilityItemIds);
  if (itemIds.length === 0) return false;

  const tagsQuery = get($inventoryTagsByItemIds);
  return tagsQuery.isLoading;
});

// 🟢🟢🟢 DERIVED DATA - Calculated inventory availabilities
export const $itemAvailabilities = atom<ItemAvailability[]>((get) => {
  const itemIds = get($inventoryAvailabilityItemIds);
  const itemsById = get($itemsById);
  const { data: tagsData } = get($inventoryTagsByItemIds);

  if (itemIds.length === 0) {
    return [];
  }

  const entries = tagsData?.inventoryTags.entries ?? [];

  // Group tags by itemId and sum netQuantity using reduce (single pass)
  const tagsByItemId = entries.reduce<
    Record<string, { itemName: string; netQuantity: number }>
  >((acc, tag) => {
    if (!acc[tag.item.id]) {
      acc[tag.item.id] = {
        itemName: tag.item.name,
        netQuantity: 0,
      };
    }

    acc[tag.item.id].netQuantity += tag.netQuantity;
    return acc;
  }, {});

  // Map itemIds to availability objects
  return itemIds.map((itemId) => {
    const tagData = tagsByItemId[itemId];
    return {
      itemId,
      itemName: tagData?.itemName || itemsById[itemId]?.name || 'Unknown Item',
      looseWeight: Math.floor(Math.random() * 5000), // TODO: Replace with actual loose weight data from backend
      taggedWeight: tagData?.netQuantity ?? 0,
    };
  });
});
